
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { formatCurrency } from "@/lib/portfolioCalculations";
import { formatPercentage } from "@/utils/formatting";
import Image from "@/components/ui/image";
import { ASSET_CLASS_ICONS, ASSET_CLASS_DISPLAY_NAMES } from "@/constants/investmentIcons";
import { isSupabasePublicStorageUrl } from "@/utils/image-storage/remote";

interface FundCardProps {
  fund: any;
  ytdPerformance?: {
    performance_value: number;
  } | null;
  priority?: boolean;
}

export function FundCard({ fund, ytdPerformance, priority = false }: FundCardProps) {
  const navigate = useNavigate();
  
  const handleCardClick = () => {
    navigate(`/funds/${fund.id}`);
  };
  
  // Get asset class icon
  const AssetClassIcon = fund.asset_class && ASSET_CLASS_ICONS[fund.asset_class.toLowerCase()]
    ? ASSET_CLASS_ICONS[fund.asset_class.toLowerCase()]
    : ASSET_CLASS_ICONS.alternative;
  
  // Determine if a custom image background or gradient should be used
  const hasCustomImage = !!fund.image_url || !!fund.fund_image_url;
  const imageUrl: string | undefined = fund.fund_image_url || fund.image_url || undefined;
  
  // Build raw image URL (no transformations)
  const isSupabaseImage = isSupabasePublicStorageUrl(imageUrl);
  const displaySrc = imageUrl;
  const sizes = "(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw";
  
  // Default gradient for headers without images
  const defaultGradient = 'bg-gradient-to-r from-slate-700 to-slate-900';
  
  // Format investment type and strategy subtitle
  const investmentTypeDisplay = fund.investment_type || 'Alternative';
  const strategySubtitle = `${investmentTypeDisplay}${fund.investment_strategy ? ` | ${fund.investment_strategy}` : ''}`;
  
  return (
    <Card 
      className="overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer h-full flex flex-col"
      onClick={handleCardClick}
    >
      <div 
        className={`h-32 relative overflow-hidden ${!hasCustomImage ? defaultGradient : ''}`} 
      >
        {hasCustomImage && displaySrc && (
          <div className="absolute inset-0 w-full h-full">
            <Image 
              src={displaySrc} 
              alt={fund.fund_name} 
              className="w-full h-full"
              objectFit="cover"
              objectPosition="center"
              optimizationMode="format"
              loading={priority ? 'eager' : 'lazy'}
              decoding="async"
              priority={priority}
              showSkeleton
              enableOptimization={true}
              fallbackSrc={isSupabaseImage ? imageUrl : undefined}
            />
          </div>
        )}
        
        {AssetClassIcon && (
          <div className="absolute top-3 right-3 bg-background/80 p-1.5 rounded-full">
            <AssetClassIcon className="h-5 w-5 text-foreground" />
          </div>
        )}
      </div>
      
      <CardHeader className="pb-0 pt-4">
        <h3 className="text-lg font-semibold line-clamp-2">{fund.fund_name}</h3>
        <p className="text-sm text-muted-foreground mt-1">{strategySubtitle}</p>
      </CardHeader>
      
      <CardContent className="space-y-3 pb-4 pt-3 flex-grow">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <p className="text-sm text-muted-foreground">Fund Size</p>
            <p className="font-medium">
              {typeof fund.fund_size === 'number' 
                ? formatCurrency(fund.fund_size)
                : fund.fund_size || "Not specified"}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">YTD Return</p>
            <p className={`font-medium ${ytdPerformance?.performance_value && ytdPerformance.performance_value > 0 ? 'text-green-600' : ytdPerformance?.performance_value && ytdPerformance.performance_value < 0 ? 'text-destructive' : ''}`}>
              {ytdPerformance?.performance_value !== undefined
                ? formatPercentage(ytdPerformance.performance_value)
                : "Not available"}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
